import { COLORS, TYPOGRAPHY } from '@/app/constants';
import { therapistAge } from '@/app/constants/therapistAge';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

type TherapistAgeSelectorProps = {
  value: string | undefined;
  set: (id: string | undefined) => void;
};


const TherapistAgeSelector: FC<TherapistAgeSelectorProps> = ({
  value,
  set
}) => {
  const handleNumberClick = (id: string | undefined) => {
    set(id);
  };

  const [thumbPosition, setThumbPosition] = useState<number>(0);
  const isDragging = useRef<boolean>(false);
  const sliderRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const index = therapistAge.findIndex(age => age.id === value);
    if (index !== -1) {
      setThumbPosition((index / (therapistAge.length - 1)) * 100);
    }
  }, [value]);

  const startDrag = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    isDragging.current = true;
    event.preventDefault();
    onMove(event);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', endDrag);
    window.addEventListener('touchcancel', endDrag);
  };

  const endDrag = () => {
    isDragging.current = false;

    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', endDrag);
    window.removeEventListener('touchmove', handleTouchMove);
    window.removeEventListener('touchend', endDrag);
    window.removeEventListener('touchcancel', endDrag);
  };

  const onMove = (event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging.current || !sliderRef.current) return;

    event.preventDefault();

    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const rect = sliderRef.current.getBoundingClientRect();
    const offsetX = clientX - rect.left;

    const newPosition = Math.min(Math.max(offsetX / rect.width, 0), 1);
    const newIndex = Math.round(newPosition * (therapistAge.length - 1));

    set(therapistAge[newIndex].id);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (isDragging.current) {
      onMove(event as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (isDragging.current) {
      onMove(event as unknown as React.TouchEvent<HTMLDivElement>);
    }
  };

  return (
    <Container>
      <NumbersContainer>
        {therapistAge.map(({ id, name }, index) => (
          <NumberItem
            key={`${id}-${index}`}
            $isSelected={id === value}
            onClick={() => handleNumberClick(id)}
          >
            {name}
          </NumberItem>
        ))}
      </NumbersContainer>
      <SliderContainer
        ref={sliderRef}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
      >
        <Slider/>
        <ClickArea position={thumbPosition} />
        <Thumb position={thumbPosition} />
      </SliderContainer>
    </Container>
  );

};

export default TherapistAgeSelector;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const NumbersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const NumberItem = styled.div<{ $isSelected: boolean }>`
  cursor: pointer;
  color: ${({ $isSelected }) => ($isSelected ? COLORS.primary300 : COLORS.grayscale600)};
  transition: color 0.3s ease;
  ${TYPOGRAPHY.disclaimerMedium12};
  width: 60px;
  text-align: center;

  &:hover {
    color: ${COLORS.primary300};
  }
`;

const SliderContainer = styled.div`
  position: relative;
  height: 20px;
  width: 80%;
`;

const Slider = styled.div`
  position: absolute;
  height: 4px;
  width: 100%;
  top: 10px;
  background: ${COLORS.primary300};
  border-radius: 5px;
`;

const ClickArea = styled.div<{ position: number }>`
  position: absolute;
  left: ${({ position }) => position}%;
  top: -2px;
  width: 30px;
  height: 30px;
  background: transparent;
  cursor: pointer;
  transition: left 0.1s ease;
  transform: translateX(-50%);
  z-index: 1;
`;

const Thumb = styled.div<{ position: number }>`
  position: absolute;
  left: ${({ position }) => position}%;
  top: 4px;
  width: 15px;
  height: 15px;
  background: ${COLORS.primary300};
  border-radius: 50%;
  cursor: pointer;
  transition: left 0.1s ease;
  transform: translateX(-50%);
`;
